import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Markdown } from '../ui';

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

/**
 * Painel de chat de IA REUTILIZÁVEL (apresentação pura).
 *
 * Mostra a conversa, o input e — quando há um TRECHO SELECIONADO no editor — um aviso
 * de que a mensagem vai com foco nele. O `transport` (como a mensagem é enviada e a
 * resposta obtida) fica no caller via `onSend`, então o MESMO componente serve a base
 * de docs (threads/agentes) e a biblioteca de skills (edição por IA). Correção de
 * comportamento aqui vale nos dois lugares.
 */
export function AiChatPanel({
  messages,
  busy = false,
  selection,
  onClearSelection,
  onSend,
  disabled = false,
  headerExtra,
  title = '💬 IA',
  placeholder = 'peça uma alteração…',
  emptyHint,
}: {
  messages: ChatTurn[];
  busy?: boolean;
  /** Texto do trecho selecionado no editor (se houver) — enviado com foco. */
  selection?: string | null;
  onClearSelection?: () => void;
  onSend: (text: string) => void;
  disabled?: boolean;
  /** Slot no cabeçalho (ex: seletor de conversa/agente na base de docs). */
  headerExtra?: ReactNode;
  title?: string;
  placeholder?: string;
  emptyHint?: string;
}) {
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, busy]);

  function submit() {
    const text = input.trim();
    if (!text || busy || disabled) return;
    setInput('');
    onSend(text);
  }

  return (
    <div className="ai-chat">
      <div className="ai-chat-head">
        <strong style={{ fontSize: 13 }}>{title}</strong>
        {headerExtra}
      </div>

      <div className="ai-chat-msgs">
        {messages.length === 0 && !busy && (
          <div className="ai-chat-empty">{emptyHint ?? 'Peça uma alteração ou tire uma dúvida.'}</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`ai-chat-msg is-${m.role}`}>
            <div className="ai-chat-role">{m.role === 'user' ? 'você' : 'IA'}</div>
            {m.role === 'assistant' ? <Markdown text={m.content} /> : <div className="ai-chat-usertext">{m.content}</div>}
          </div>
        ))}
        {busy && (
          <div className="ai-chat-msg is-assistant">
            <div className="ai-chat-role">IA</div>
            <Markdown text="…" />
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {selection && selection.trim() && (
        <div className="ai-chat-selection" title={selection}>
          <span className="ai-chat-selection-label">trecho selecionado ({selection.length} car.)</span>
          {onClearSelection && (
            <button type="button" className="ai-chat-selection-clear" onClick={onClearSelection} title="Limpar foco">
              ✕
            </button>
          )}
        </div>
      )}

      <div className="ai-chat-input">
        <input
          placeholder={disabled ? 'sem permissão para editar' : placeholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          disabled={busy || disabled}
        />
        <Button size="sm" variant="primary" onClick={submit} disabled={busy || disabled || !input.trim()}>
          {busy ? '…' : 'enviar'}
        </Button>
      </div>
    </div>
  );
}
